/**
 * Phase 132B — Pilot Cap Reached Modal
 * ------------------------------------
 * Celebratory, NOT corporate. Triggered when:
 *  - Academy_owner on pilot tries to create a stream past the hard cap (402 → open modal)
 *  - Academy_owner lands on dashboard/studio after hitting soft cap (proactive nudge)
 *
 * Shows the same rich monthly usage stats the upgrade email shows, then a
 * single primary CTA to /livex/subscription.
 *
 * UX doctrine: sports achievement energy, high-contrast, no opacity tricks.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import { Trophy, X, Sparkles, Eye, Radio, Star, Crown, Loader2 } from 'lucide-react';

export default function LivexPilotCapModal({ open, onClose, hardCapMode = false }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/livex/me/usage-summary')
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const headline = hardCapMode
    ? '🏆 Your academy is on fire'
    : '🚀 Your academy is gaining momentum';
  const subline = hardCapMode
    ? "You've used every stream this month. Upgrade to keep broadcasting."
    : "You're past the Free Pilot limit. Upgrade now or finish with your grace stream.";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/85 backdrop-blur"
      data-testid="livex-pilot-cap-modal"
      role="dialog"
      aria-labelledby="livex-cap-modal-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[#07111F] border-2 border-[#FF5A1F] rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 24px 60px rgba(255,90,31,0.35)' }}
      >
        {/* Cinematic hero header */}
        <div className="relative px-6 py-7 sm:px-8 sm:py-8 overflow-hidden" style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #f97316 100%)',
        }}>
          <div className="absolute inset-0 opacity-30" style={{
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 50%)',
          }} />
          <button
            onClick={onClose}
            data-testid="livex-pilot-cap-modal-close"
            className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-black/30 hover:bg-black/50 grid place-items-center text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative">
            <div className="text-[11px] font-black tracking-[0.25em] uppercase text-white/95 mb-2">
              <Crown className="inline h-3.5 w-3.5 mr-1.5" />
              StreamX Pro Upgrade
            </div>
            <h2 id="livex-cap-modal-title" className="text-2xl sm:text-3xl font-black text-white leading-tight" data-testid="livex-pilot-cap-modal-headline">
              {headline}
            </h2>
            <p className="mt-1.5 text-sm sm:text-base font-bold text-white/95 leading-snug">{subline}</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 sm:p-7">
          {loading || !summary ? (
            <div className="py-10 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-[#FF5A1F]" />
              <div className="livex-body-text mt-3" style={{ fontSize: '13px' }}>Crunching your numbers…</div>
            </div>
          ) : (
            <>
              <div className="livex-label mb-3">This month at {summary.academy_name || 'your academy'}</div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3.5 bg-[#0c1a2e] border-2 border-[#22314F] rounded-xl">
                  <Radio className="h-4 w-4 text-rose-400 mb-1.5" />
                  <div className="text-2xl font-black text-white tabular-nums leading-none">{summary.usage.streams_this_month}</div>
                  <div className="livex-label mt-1.5" style={{ marginBottom: 0 }}>Streams</div>
                </div>
                <div className="p-3.5 bg-[#0c1a2e] border-2 border-[#22314F] rounded-xl">
                  <Eye className="h-4 w-4 text-[#FF5A1F] mb-1.5" />
                  <div className="text-2xl font-black text-white tabular-nums leading-none">{summary.usage.peak_concurrent}</div>
                  <div className="livex-label mt-1.5" style={{ marginBottom: 0 }}>Peak Audience</div>
                </div>
                <div className="p-3.5 bg-[#0c1a2e] border-2 border-[#22314F] rounded-xl">
                  <Star className="h-4 w-4 text-amber-400 mb-1.5" />
                  <div className="text-2xl font-black text-white tabular-nums leading-none">{summary.usage.total_highlights}</div>
                  <div className="livex-label mt-1.5" style={{ marginBottom: 0 }}>Highlights</div>
                </div>
                <div className="p-3.5 bg-[#0c1a2e] border-2 border-[#22314F] rounded-xl">
                  <Trophy className="h-4 w-4 text-amber-400 mb-1.5" />
                  <div className="text-2xl font-black text-white tabular-nums leading-none">{summary.usage.total_viewers}</div>
                  <div className="livex-label mt-1.5" style={{ marginBottom: 0 }}>Total Viewers</div>
                </div>
              </div>

              {summary.usage.top_match?.title && (
                <div className="mb-5 p-3.5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] rounded-xl">
                  <div className="livex-label mb-1">Most Watched Match</div>
                  <div className="text-sm font-black text-white truncate">{summary.usage.top_match.title}</div>
                  <div className="text-[11px] font-bold text-[#94A3B8] mt-0.5">
                    Peak {summary.usage.top_match.peak_viewers} viewers · <span className="capitalize">{summary.usage.top_match.sport || 'match'}</span>
                  </div>
                </div>
              )}

              <div className="mb-5">
                <div className="livex-label mb-2">Pro unlocks:</div>
                <ul className="space-y-1.5">
                  {[
                    'Unlimited streams',
                    'Replay recordings + highlight reels',
                    'Multi-platform simulcast (soon)',
                    'Remove StreamX branding',
                    'Priority support',
                  ].map((f, i) => (
                    <li key={i} className="text-sm text-white font-medium flex items-center gap-2">
                      <Sparkles className="h-3.5 w-3.5 text-[#FF5A1F] flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/streamx/subscription"
                data-testid="livex-pilot-cap-modal-upgrade"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 text-base font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #dc2626, #f97316)',
                  boxShadow: '0 8px 24px rgba(255,90,31,0.35)',
                  minHeight: '52px',
                }}
              >
                <Crown className="h-4 w-4" /> Upgrade to StreamX Pro
              </Link>
              <div className="text-center text-[11px] font-bold text-[#94A3B8] mt-3">
                ₹299/month · cancel anytime · or ₹2,999/year (save 17%)
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
